import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  obtenerPrograma,
  obtenerVersiones,
  activarVersion,
  eliminarVersion
} from '../../servicios/programaService';
import ImportarPrograma from './ImportarPrograma';
import GanttPrograma from './GanttPrograma';
import { hoyEnSantiago } from '../utils/fechaPrograma';
import {
  ESTADO_LABEL,
  computarMetaActividades,
  calcularAvancesAgregados,
  estadoDesdeAvance,
  calcularAvanceEsperado,
  colorDesdeAvance,
  esVisible
} from '../utils/actividadesPrograma';
import '../estilos/Programa.css';

function formatFecha(fecha) {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleDateString('es-CL', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
}

export default function MatrizPrograma() {
  const [datos, setDatos] = useState({ programa: null, secciones: [], actividades: [] });
  const [versiones, setVersiones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarImportar, setMostrarImportar] = useState(false);
  const [vista, setVista] = useState('tabla'); // 'tabla' | 'gantt'
  const [barrasListas, setBarrasListas] = useState(false); // dispara animación inicial
  const [error, setError] = useState('');

  // Estado de colapso: Set de IDs/wbsCodes colapsados
  const [seccionesColapsadas, setSeccionesColapsadas] = useState(new Set());
  const [actividadesColapsadas, setActividadesColapsadas] = useState(new Set());

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const [prog, vers] = await Promise.all([obtenerPrograma(), obtenerVersiones()]);
      setDatos(prog);
      setVersiones(vers.versiones || []);
    } catch (err) {
      setError('Error al cargar el programa');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  // Dispara animación de barras 60ms después de que los datos lleguen
  useEffect(() => {
    if (!cargando && datos.programa) {
      setBarrasListas(false);
      const t = setTimeout(() => setBarrasListas(true), 60);
      return () => clearTimeout(t);
    }
  }, [cargando, datos.programa]);

  // Avances agregados calculados (ponderado por duración, bottom-up)
  const { avanceCalc } = useMemo(
    () => calcularAvancesAgregados(datos.actividades),
    [datos.actividades]
  );

  // Medianoche hora Santiago, para calcular el avance esperado de cada actividad
  const hoy = useMemo(() => hoyEnSantiago(), []);

  // Árbol de actividades por sección (memoizado para no recalcular en cada render)
  const arbolPorSeccion = useMemo(() => {
    const map = {};
    for (const sec of datos.secciones) {
      const acts = datos.actividades
        .filter(a => a.seccion === sec._id || a.seccion?._id === sec._id)
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
      map[sec._id] = { seccion: sec, ...computarMetaActividades(acts) };
    }
    return map;
  }, [datos]);

  // Al cargar datos nuevos: colapsar todas las secciones y grupos padre
  useEffect(() => {
    if (datos.secciones.length === 0) return;
    setSeccionesColapsadas(new Set(datos.secciones.map(s => s._id)));
    const todosParents = new Set();
    for (const { parentCodes } of Object.values(arbolPorSeccion)) {
      for (const code of parentCodes) todosParents.add(code);
    }
    setActividadesColapsadas(todosParents);
  }, [datos.secciones, arbolPorSeccion]);

  const toggleSeccion = (secId) => {
    setSeccionesColapsadas(prev => {
      const next = new Set(prev);
      if (next.has(secId)) next.delete(secId); else next.add(secId);
      return next;
    });
  };

  const toggleActividadGrupo = (wbsCode) => {
    setActividadesColapsadas(prev => {
      const next = new Set(prev);
      if (next.has(wbsCode)) next.delete(wbsCode); else next.add(wbsCode);
      return next;
    });
  };

  const expandirTodo = () => {
    setSeccionesColapsadas(new Set());
    setActividadesColapsadas(new Set());
  };

  const colapsarTodo = () => {
    setSeccionesColapsadas(new Set(datos.secciones.map(s => s._id)));
    const todosParents = new Set();
    for (const { parentCodes } of Object.values(arbolPorSeccion)) {
      for (const code of parentCodes) todosParents.add(code);
    }
    setActividadesColapsadas(todosParents);
  };

  const handleImportado = () => {
    setMostrarImportar(false);
    cargarDatos();
  };

  const handleActivarVersion = async (versionId) => {
    try {
      await activarVersion(versionId);
      cargarDatos();
    } catch (err) {
      setError('Error al activar versión');
    }
  };

  const handleEliminarVersion = async (versionId, versionNum) => {
    if (!window.confirm(`¿Eliminar la versión ${versionNum}? Esta acción no se puede deshacer.`)) return;
    try {
      await eliminarVersion(versionId);
      cargarDatos();
    } catch (err) {
      setError('Error al eliminar versión');
    }
  };

  // Resumen global — solo hojas (sin hijos), ponderado por duración
  const total = datos.actividades.length;

  // Set de todos los wbsCodes que son padres (reutiliza lo calculado en arbolPorSeccion)
  const todosParentCodes = useMemo(() => {
    const s = new Set();
    for (const { parentCodes } of Object.values(arbolPorSeccion)) {
      for (const code of parentCodes) s.add(code);
    }
    return s;
  }, [arbolPorSeccion]);

  const hojas = useMemo(
    () => datos.actividades.filter(a => !a.wbsCode || !todosParentCodes.has(a.wbsCode)),
    [datos.actividades, todosParentCodes]
  );

  const completadas = hojas.filter(a => (avanceCalc[a.wbsCode] ?? a.porcentajeAvance) >= 100).length;
  const avanceProm  = useMemo(() => {
    if (!hojas.length) return 0;
    let sumDur = 0, sumAvance = 0;
    for (const a of hojas) {
      const dur = a.duracionBase || 1;
      sumDur += dur;
      sumAvance += (avanceCalc[a.wbsCode] ?? a.porcentajeAvance ?? 0) * dur;
    }
    return sumDur > 0 ? Math.round(sumAvance / sumDur) : 0;
  }, [hojas, avanceCalc]);

  if (cargando) {
    return (
      <div className="programa-container">
        <div className="programa-spinner-wrap">
          <div className="programa-spinner" />
          <span className="programa-spinner-texto">Cargando actividades…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="programa-container">
      {/* Header */}
      <div className="programa-header">
        <h2>Programa de Obra</h2>
        <div className="programa-header-actions">
          {versiones.length > 1 && (
            <div className="programa-version-selector">
              <span>Versión activa:</span>
              <select
                value={datos.programa?._id || ''}
                onChange={e => handleActivarVersion(e.target.value)}
              >
                {versiones.map(v => (
                  <option key={v._id} value={v._id}>
                    {v.nombre} {v.esLineaBase ? '(activa)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          {datos.programa && (
            <button
              className="programa-btn programa-btn-danger"
              onClick={() => handleEliminarVersion(datos.programa._id, datos.programa.version)}
            >
              Eliminar programa
            </button>
          )}
          <button
            className="programa-btn programa-btn-primary"
            onClick={() => setMostrarImportar(true)}
          >
            {datos.programa ? 'Actualizar programa' : 'Importar programa'}
          </button>
        </div>
      </div>

      {datos.programa && (
        <div className="programa-vista-toggle">
          <button
            className={`programa-vista-btn${vista === 'tabla' ? ' activo' : ''}`}
            onClick={() => setVista('tabla')}
          >
            Tabla
          </button>
          <button
            className={`programa-vista-btn${vista === 'gantt' ? ' activo' : ''}`}
            onClick={() => setVista('gantt')}
          >
            Carta Gantt
          </button>
        </div>
      )}

      {error && (
        <div className="programa-error" style={{ marginBottom: 16 }}>
          {error}
          <button
            onClick={() => setError('')}
            style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
          >×</button>
        </div>
      )}

      {!datos.programa ? (
        <div className="programa-empty">
          <h3>Sin programa importado</h3>
          <p>Exporta tu carta Gantt desde MS Project o Primavera P6 como XML<br />y súbela aquí para comenzar a registrar avances.</p>
          <button className="programa-btn programa-btn-primary" onClick={() => setMostrarImportar(true)}>
            Importar programa
          </button>
        </div>
      ) : (
        <>
          {/* Info programa */}
          <div style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: 16 }}>
            {datos.programa.nombre} · {datos.programa.fuente === 'msproject' ? 'MS Project' : 'Primavera P6'}
            · Importado {formatFecha(datos.programa.fechaImport)}
          </div>

          {/* Resumen */}
          <div className="programa-resumen">
            <div className="programa-resumen-card">
              <div className="valor">{total}</div>
              <div className="etiqueta">Actividades</div>
            </div>
            <div className="programa-resumen-card">
              <div className="valor">{avanceProm}%</div>
              <div className="etiqueta">Avance global</div>
            </div>
            <div className="programa-resumen-card">
              <div className="valor" style={{ color: '#22c55e' }}>{completadas}</div>
              <div className="etiqueta">Completadas</div>
            </div>
          </div>

          {/* Leyenda + controles */}
          <div className="programa-toolbar">
            <div className="programa-leyenda">
              <div className="programa-leyenda-item">
                <div className="programa-leyenda-color" style={{ background: '#22c55e' }} />
                Completado
              </div>
              <div className="programa-leyenda-item">
                <div className="programa-leyenda-color" style={{ background: '#eab308' }} />
                A tiempo / leve atraso
              </div>
              <div className="programa-leyenda-item">
                <div className="programa-leyenda-color" style={{ background: '#f97316' }} />
                Atrasado
              </div>
              <div className="programa-leyenda-item">
                <div className="programa-leyenda-color" style={{ background: '#ef4444' }} />
                Muy atrasado
              </div>
            </div>
            {vista === 'tabla' && (
              <div className="programa-expand-controls">
                <button className="programa-ctrl-btn" onClick={expandirTodo}>Expandir todo</button>
                <button className="programa-ctrl-btn" onClick={colapsarTodo}>Colapsar todo</button>
              </div>
            )}
          </div>

          {vista === 'gantt' ? (
            <GanttPrograma datos={datos} hoy={hoy} />
          ) : (
          <>
          {/* Tabla */}
          <div className="programa-tabla-wrapper">
            <table className="programa-tabla">
              <thead>
                <tr>
                  <th>Actividad</th>
                  <th>Inicio base</th>
                  <th>Fin base</th>
                  <th>Duración</th>
                  <th>Avance</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(arbolPorSeccion).map(({ seccion, actividades: acts, parentCodes }) => {
                  const colapsada = seccionesColapsadas.has(seccion._id);
                  const actsFiltradas = colapsada ? [] : acts.filter(a => esVisible(a, actividadesColapsadas));

                  return (
                    <React.Fragment key={seccion._id}>
                      {/* Fila de sección (siempre visible, clickeable) */}
                      <tr
                        className="programa-fila-seccion programa-fila-toggle"
                        onClick={() => toggleSeccion(seccion._id)}
                      >
                        <td colSpan={6}>
                          <span className={`programa-chevron${colapsada ? ' colapsado' : ''}`}>▾</span>
                          {seccion.wbsCode && (
                            <span className="programa-wbs-seccion">{seccion.wbsCode}</span>
                          )}
                          <span className="programa-seccion-nombre">{seccion.nombre}</span>
                          <span className="programa-seccion-count">{acts.length} actividades</span>
                        </td>
                      </tr>

                      {/* Actividades de la sección (filtradas por visibilidad) */}
                      {actsFiltradas.map(act => {
                        const indent = 16 + act.relDepth * 18;
                        const grupoColapsado = act.esParent && actividadesColapsadas.has(act.wbsCode);

                        const avanceMostrar = act.esParent
                          ? (avanceCalc[act.wbsCode] ?? act.porcentajeAvance ?? 0)
                          : (act.porcentajeAvance ?? 0);
                        const estadoMostrar = act.esParent
                          ? estadoDesdeAvance(avanceMostrar)
                          : (act.estado || 'pendiente');
                        const esperadoMostrar = calcularAvanceEsperado(act.fechaInicioBase, act.fechaFinBase, hoy);
                        const colorMostrar = colorDesdeAvance(avanceMostrar, esperadoMostrar);

                        return (
                          <tr
                            key={act._id}
                            className={`programa-fila-actividad${act.esParent ? ' programa-fila-grupo' : ''}`}
                            onClick={() => act.esParent && toggleActividadGrupo(act.wbsCode)}
                            title={act.esParent ? 'Clic para expandir/colapsar grupo' : 'El avance se registra desde la app mobile'}
                          >
                            <td className="programa-actividad-nombre" style={{ paddingLeft: indent }}>
                              {act.esParent
                                ? <span className={`programa-chevron-sm${grupoColapsado ? ' colapsado' : ''}`}>▾</span>
                                : <span className="programa-leaf-dot" />
                              }
                              {act.wbsCode && (
                                <span className="programa-wbs">{act.wbsCode}</span>
                              )}
                              <span className={act.esParent ? 'programa-grupo-nombre' : ''}>
                                {act.nombre}
                              </span>
                            </td>
                            <td className="programa-fecha">{formatFecha(act.fechaInicioBase)}</td>
                            <td className="programa-fecha">{formatFecha(act.fechaFinBase)}</td>
                            <td style={{ color: '#64748b', fontSize: '0.82rem' }}>
                              {act.duracionBase > 0 ? `${act.duracionBase}d` : '—'}
                            </td>
                            <td>
                              <div className="programa-barra-wrapper">
                                <div className="programa-barra-track">
                                  <div
                                    className="programa-barra-fill"
                                    style={{
                                      width: barrasListas ? `${avanceMostrar}%` : '0%',
                                      background: colorMostrar,
                                      transition: 'width 0.9s cubic-bezier(0.25, 0.8, 0.25, 1)'
                                    }}
                                  />
                                </div>
                                <span className="programa-barra-pct">{avanceMostrar}%</span>
                              </div>
                            </td>
                            <td>
                              <span className={`programa-badge badge-${estadoMostrar}`}>
                                {ESTADO_LABEL[estadoMostrar] || estadoMostrar}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
          )}
        </>
      )}

      {/* Modal importar */}
      {mostrarImportar && (
        <ImportarPrograma
          onImportado={handleImportado}
          onCerrar={() => setMostrarImportar(false)}
        />
      )}

    </div>
  );
}
